import React from "react";
import {
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  Typography,
  alpha,
  useTheme,
  IconButton,
  Tooltip,
} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import HomeIcon from "@mui/icons-material/Home";
import BusinessIcon from "@mui/icons-material/Business";
import EditIcon from "@mui/icons-material/Edit";

function CustomerDataComponent({ customer, onEdit, showEditButton = false }) {
  const theme = useTheme();

  if (!customer) {
    return (
      <Card
        elevation={2}
        sx={{
          bgcolor: alpha(theme.palette.warning.light, 0.1),
          borderLeft: `4px solid ${theme.palette.warning.main}`,
        }}
      >
        <CardContent>
          <Typography variant="body2" color="text.secondary">
            No customer selected
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      elevation={2}
      sx={{
        bgcolor: alpha(theme.palette.info.main, 0.05),
        borderLeft: `4px solid ${theme.palette.info.main}`,
        transition: "all 0.3s ease",
        "&:hover": {
          boxShadow: theme.shadows[4],
        },
      }}
    >
      <CardContent>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <PersonIcon color="info" />
            <Typography variant="subtitle1" fontWeight="600" color="info.main">
              Customer Details
            </Typography>
          </Box>
          {showEditButton && onEdit && (
            <Tooltip title="Change Customer">
              <IconButton size="small" onClick={onEdit} color="info">
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        <Grid container spacing={2}>
          {/* Customer Name */}
          <Grid item xs={12}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <BusinessIcon sx={{ fontSize: 18, color: "text.secondary" }} />
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Name
                </Typography>
                <Typography variant="body1" fontWeight="600">
                  {customer.customerName || "N/A"}
                </Typography>
              </Box>
            </Box>
          </Grid>

          {/* Contact Number */}
          {customer.contactNumber && (
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <PhoneIcon sx={{ fontSize: 18, color: "text.secondary" }} />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Contact
                  </Typography>
                  <Typography variant="body2">
                    {customer.contactNumber}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          )}

          {/* Email */}
          {customer.email && (
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <EmailIcon sx={{ fontSize: 18, color: "text.secondary" }} />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Email
                  </Typography>
                  <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
                    {customer.email}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          )}

          {/* Address */}
          {customer.address && (
            <Grid item xs={12}>
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                <HomeIcon
                  sx={{ fontSize: 18, color: "text.secondary", mt: 0.5 }}
                />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Address
                  </Typography>
                  <Typography variant="body2">{customer.address}</Typography>
                </Box>
              </Box>
            </Grid>
          )}

          {/* Customer Type (if available) */}
          {customer.customerType && (
            <Grid item xs={12}>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                <Chip
                  label={customer.customerType}
                  size="small"
                  color={
                    customer.customerType === "WHOLESALE"
                      ? "secondary"
                      : "primary"
                  }
                  variant="outlined"
                />
              </Box>
            </Grid>
          )}

          {/* Additional Info */}
          {customer.additionalInfo && (
            <Grid item xs={12}>
              <Box
                sx={{
                  p: 1.5,
                  bgcolor: alpha(theme.palette.background.default, 0.6),
                  borderRadius: 1,
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  Notes
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  {customer.additionalInfo}
                </Typography>
              </Box>
            </Grid>
          )}
        </Grid>
      </CardContent>
    </Card>
  );
}

export default CustomerDataComponent;
